import {
  Text,
  Tooltip,
  TextProps as ChakraTextProps,
  TooltipProps as ChakraTooltipProps,
} from '@chakra-ui/react';
import React, {
  useRef,
  useState,
  ReactNode,
  useLayoutEffect,
  useCallback,
  forwardRef,
} from 'react';
import { TEXT_STYLES } from '@/ui-library/design-system';

interface TextProps extends ChakraTextProps {
  children: React.ReactNode;
  variant?: keyof typeof TEXT_STYLES;
  size?: any;
  tooltipText?: string | ReactNode;
  enableTooltip?: boolean;
  tooltipProps?: ChakraTooltipProps;
  isTextTruncatedDefault?: boolean;
  tooltipString?: string;
  isTruncated?: boolean;
}

export const KText = forwardRef<HTMLDivElement, TextProps>(
  (
    {
      children,
      variant = 'paragraph',
      tooltipText,
      enableTooltip = false,
      size,
      isTextTruncatedDefault = false,
      tooltipProps = {
        arrowSize: 8,
        hasArrow: true,
        placement: 'bottom',
        variant: 'default',
        children: undefined,
      },
      // tooltipString = undefined,
      isTruncated,
      ...props
    },
    externalRef
  ) => {
    const internalRef = useRef<HTMLDivElement>(null);

    // Merge the external forwarded ref with our internal ref
    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        internalRef.current = node;
        if (typeof externalRef === 'function') {
          externalRef(node);
        } else if (externalRef) {
          (
            externalRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = node;
        }
      },
      [externalRef]
    );

    const [isTextTruncated, setIsTextTruncated] = useState(
      isTextTruncatedDefault
    );

    useLayoutEffect(() => {
      const checkTruncation = () => {
        const element = internalRef.current;
        if (element) {
          setIsTextTruncated(element.scrollWidth > element.clientWidth);
        }
      };

      // Use ResizeObserver to detect when the element is actually rendered
      let resizeObserver: ResizeObserver | null = null;

      const setupObserver = () => {
        if (internalRef.current) {
          resizeObserver = new ResizeObserver(() => {
            checkTruncation();
          });
          resizeObserver.observe(internalRef.current);
        }
      };

      // Try to set up observer immediately
      setupObserver();

      // If element isn't ready, try again after a short delay
      if (!internalRef.current) {
        setTimeout(setupObserver, 0);
      }

      // Initial check
      checkTruncation();

      window.addEventListener('resize', checkTruncation);

      return () => {
        window.removeEventListener('resize', checkTruncation);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }, [children]);

    // size & variant can't coexist. We can use size to override variant
    const variantStyles =
      TEXT_STYLES?.[variant as keyof typeof TEXT_STYLES] || {};

    const textStyles = size ? { size } : variantStyles;

    const tooltipLabel =
      isTextTruncated || isTextTruncatedDefault
        ? tooltipText || tooltipProps?.children || children
        : '';

    // Chakra v2 removed isTruncated; replicate with equivalent CSS props
    const truncationProps = isTruncated
      ? {
          overflow: 'hidden' as const,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap' as const,
        }
      : {};

    const renderText = () => (
      <Text ref={mergedRef} {...textStyles} {...truncationProps} {...props}>
        {children}
      </Text>
    );

    if (enableTooltip) {
      return (
        <Tooltip label={tooltipLabel} {...tooltipProps}>
          {renderText()}
        </Tooltip>
      );
    }
    return renderText();
  }
);
